"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeviceHealthTimelineEventSchema = exports.DeviceHealthProblemSchema = exports.DeviceHealthSummarySchema = exports.DeviceHealthDeviceStatSchema = exports.DEVICE_HEALTH_WINDOW_MS = exports.DeviceHealthWindowSchema = exports.DeviceHealthScopeSchema = exports.DeviceHealthQueueMessageSchema = exports.DeviceHealthIdentitySchema = exports.DeviceHealthBatchSchema = exports.DeviceHealthEventSchema = exports.DeviceHealthDeviceSchema = exports.DeviceHealthDetailSchema = exports.DEVICE_HEALTH_LIMITS = exports.DeviceHealthProfileSchema = exports.DEVICE_HEALTH_PROFILES = exports.PLANNED_EXIT_REASONS = exports.isRollupOnlyKind = exports.ROLLUP_ONLY_DEVICE_HEALTH_KINDS = exports.isCrashKind = exports.URGENT_DEVICE_HEALTH_KINDS = exports.DeviceHealthKindSchema = exports.DEVICE_HEALTH_KINDS = void 0;
exports.crashFreePct = crashFreePct;
const zod_1 = require("zod");
/**
 * DEVICE HEALTH — silent telemetry from POS tills.
 * Spec: docs/superpowers/specs/2026-10-04-device-health-service-design.md
 *
 * The envelope a till POSTs to device-health-service, the message ingest hands
 * the worker, and the read DTOs the super-admin panels render.
 *
 * PRIVACY (§11): `detail` is a flat record of primitives — a nested object can
 * never ride along, so a request/response body cannot be attached by mistake.
 * No card data beyond amount + last4, no names, emails or phone numbers;
 * `employeeId` only.
 */
exports.DEVICE_HEALTH_KINDS = [
    'crash.native', 'crash.exit', 'crash.renderer',
    'js.error', 'js.rejection',
    'net.fail', 'print.fail', 'usb.drop', 'pay.anomaly',
    'warn', 'session.start',
    // Owner decisions 2026-10-04: a 5-minute foreground heartbeat (offline
    // detection, "devices reporting"), and deliberate restarts (fleet restart,
    // APK update, OTA apply, kiosk restart) reported as what they are — never
    // as crash.exit.
    'session.heartbeat', 'session.planned_restart',
];
exports.DeviceHealthKindSchema = zod_1.z.enum(exports.DEVICE_HEALTH_KINDS);
/** Flushed immediately on the till, kept first by the ingest throttle, and they page an admin. */
exports.URGENT_DEVICE_HEALTH_KINDS = [
    'crash.native', 'crash.exit', 'crash.renderer', 'pay.anomaly',
];
const isCrashKind = (k) => k.startsWith('crash.');
exports.isCrashKind = isCrashKind;
/** Counted in rollups and device state only — never a problem, a ticket or an email. */
exports.ROLLUP_ONLY_DEVICE_HEALTH_KINDS = [
    'session.start', 'session.heartbeat', 'session.planned_restart',
];
const isRollupOnlyKind = (k) => exports.ROLLUP_ONLY_DEVICE_HEALTH_KINDS.includes(k);
exports.isRollupOnlyKind = isRollupOnlyKind;
/** Why the till exited on purpose. Written as a marker before the exit; matched at next boot. */
exports.PLANNED_EXIT_REASONS = ['fleet_restart', 'app_update', 'ota_apply', 'kiosk_restart', 'clear_data'];
/** `other` = native hardware the till's classifier does not recognise (control-center/deviceKind.ts). */
/* 'sunmi_t3' (2026-10-14, additive): the SUNMI T3 till — primary Tap to Pay +
   backup reader. Its heartbeat `detail` carries ttpState / ttpAppVersion /
   backupState as plain keys (detail is already a flat record). */
exports.DEVICE_HEALTH_PROFILES = ['k10', 'a10', 'vector', 'web', 'other', 'sunmi_t3'];
exports.DeviceHealthProfileSchema = zod_1.z.enum(exports.DEVICE_HEALTH_PROFILES);
exports.DEVICE_HEALTH_LIMITS = {
    maxBatchEvents: 50,
    maxBatchBytes: 64 * 1024,
    perDevicePerMinute: 60,
    deviceBufferMax: 500,
    dedupeWindowMs: 60000,
    flushIntervalMs: 30000,
    rawEventTtlDays: 30,
    crashEmailThrottleMs: 60 * 60000,
    /** A device timestamp outside [receivedAt - past, receivedAt + future] is replaced by receivedAt for bucketing. */
    clockSkewPastMs: 7 * 24 * 60 * 60000,
    clockSkewFutureMs: 5 * 60000,
    maxAffectedDevices: 20,
    /** Foreground heartbeat cadence; rides the normal 30 s flush, never its own request. */
    heartbeatIntervalMs: 5 * 60000,
    /** Heartbeat rows in the events table expire fast — they exist for idempotency, not the timeline. */
    heartbeatTtlDays: 2,
    /** Three missed heartbeats = offline in the panels. */
    offlineAfterMs: 15 * 60000,
    /** A planned-exit marker within this distance of an ApplicationExitInfo timestamp makes it planned. */
    plannedExitMatchMs: 2 * 60000,
};
const DetailValue = zod_1.z.union([zod_1.z.string().max(2000), zod_1.z.number(), zod_1.z.boolean(), zod_1.z.null()]);
exports.DeviceHealthDetailSchema = zod_1.z
    .record(zod_1.z.string().max(40), DetailValue)
    .refine((d) => Object.keys(d).length <= 20, { message: 'detail carries more than 20 keys' });
exports.DeviceHealthDeviceSchema = zod_1.z.object({
    /** = terminalId = registerId (device-config-service/src/lib/deviceProfile.ts). */
    deviceId: zod_1.z.string().min(1).max(128),
    deviceUnitId: zod_1.z.string().max(128).optional(),
    model: zod_1.z.string().max(80).default('unknown'),
    profile: exports.DeviceHealthProfileSchema,
    os: zod_1.z.string().max(80).default('unknown'),
    appVersion: zod_1.z.string().max(64).default('unknown'),
    bundleVersion: zod_1.z.string().max(64).optional(),
});
exports.DeviceHealthEventSchema = zod_1.z.object({
    eventId: zod_1.z.string().min(8).max(64),
    kind: exports.DeviceHealthKindSchema,
    /** Device clock, epoch ms. Part of the idempotency key — never rewritten. */
    ts: zod_1.z.number().int().positive(),
    /** How many identical events the 60 s device dedupe folded into this one. */
    count: zod_1.z.number().int().positive().max(100000).default(1),
    fingerprintHint: zod_1.z.string().max(200).optional(),
    message: zod_1.z.string().max(500),
    detail: exports.DeviceHealthDetailSchema.default({}),
    screen: zod_1.z.string().max(120).default('unknown'),
    device: exports.DeviceHealthDeviceSchema,
    tenantId: zod_1.z.string().min(1).max(128),
    locationId: zod_1.z.string().min(1).max(128),
    registerId: zod_1.z.string().min(1).max(128),
    employeeId: zod_1.z.string().max(128).optional(),
});
/** Events are `unknown` here on purpose: ingest judges each one alone, so one bad event never sinks the batch. */
exports.DeviceHealthBatchSchema = zod_1.z.object({
    events: zod_1.z.array(zod_1.z.unknown()).min(1).max(exports.DEVICE_HEALTH_LIMITS.maxBatchEvents),
});
exports.DeviceHealthIdentitySchema = zod_1.z.object({
    via: zod_1.z.enum(['device', 'session']),
    tenantId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    deviceId: zod_1.z.string().min(1),
    employeeId: zod_1.z.string().optional(),
});
exports.DeviceHealthQueueMessageSchema = zod_1.z.object({
    v: zod_1.z.literal(1),
    receivedAt: zod_1.z.number().int().positive(),
    auth: exports.DeviceHealthIdentitySchema,
    events: zod_1.z.array(exports.DeviceHealthEventSchema).min(1).max(exports.DEVICE_HEALTH_LIMITS.maxBatchEvents),
});
// ---------------------------------------------------------------------------
// Read side — GET /summary, /problems, /devices/{deviceId}/events
// ---------------------------------------------------------------------------
exports.DeviceHealthScopeSchema = zod_1.z.enum(['fleet', 'tenant', 'device', 'version']);
/** `30d` exists for the device timeline's "full history" (events TTL is 30 days). */
exports.DeviceHealthWindowSchema = zod_1.z.enum(['1h', '24h', '7d', '30d']);
exports.DEVICE_HEALTH_WINDOW_MS = {
    '1h': 3600000,
    '24h': 86400000,
    '7d': 604800000,
    '30d': 2592000000,
};
/** 1 − sessions with a crash ÷ session.start count, one decimal. Null when there were no sessions. */
function crashFreePct(sessions, crashes) {
    if (!Number.isFinite(sessions) || sessions <= 0)
        return null;
    const c = Math.min(Math.max(0, Number(crashes) || 0), sessions);
    return Math.round((1 - c / sessions) * 1000) / 10;
}
const Int = zod_1.z.number().int().nonnegative();
exports.DeviceHealthDeviceStatSchema = zod_1.z.object({
    errors24h: Int,
    crashes24h: Int,
    lastCrashAt: zod_1.z.number().int().nullable(),
    lastEventAt: zod_1.z.number().int().nullable(),
});
exports.DeviceHealthSummarySchema = zod_1.z.object({
    scope: exports.DeviceHealthScopeSchema,
    id: zod_1.z.string().nullable(),
    window: exports.DeviceHealthWindowSchema,
    generatedAt: zod_1.z.number().int(),
    devicesReporting: Int,
    devicesKnown: Int,
    sessions: Int,
    crashes: Int,
    crashFreePct: zod_1.z.number().nullable(),
    errors: Int,
    errorsPerHour: zod_1.z.number().nonnegative(),
    errorsPerSession: zod_1.z.number().nonnegative().nullable(),
    payAnomalies: Int,
    printFailures: Int,
    openTickets: Int,
    blockingTickets: Int,
    byKind: zod_1.z.record(zod_1.z.string(), Int),
    series: zod_1.z.array(zod_1.z.object({ hour: zod_1.z.string(), crashes: Int, server: Int, printer: Int, other: Int })),
    worstDevices: zod_1.z.array(zod_1.z.object({
        deviceId: zod_1.z.string(), tenantId: zod_1.z.string().nullable(), registerId: zod_1.z.string().nullable(),
        errors: Int, crashes: Int, lastCrashAt: zod_1.z.number().int().nullable(),
    })),
    worstStores: zod_1.z.array(zod_1.z.object({
        tenantId: zod_1.z.string(), tenantName: zod_1.z.string().nullable(), devices: Int,
        sessions: Int, crashes: Int, errors: Int, crashFreePct: zod_1.z.number().nullable(),
    })),
    /** deviceId → 24 h stats. Filled for fleet and tenant scope; drives the Fleet table's two columns. */
    devices: zod_1.z.record(zod_1.z.string(), exports.DeviceHealthDeviceStatSchema),
});
exports.DeviceHealthProblemSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    tenantName: zod_1.z.string().nullable(),
    fingerprint: zod_1.z.string(),
    kind: exports.DeviceHealthKindSchema,
    title: zod_1.z.string(),
    count: Int,
    devices: Int,
    firstSeen: zod_1.z.number().int(),
    lastSeen: zod_1.z.number().int(),
    versions: zod_1.z.array(zod_1.z.string()),
    issueId: zod_1.z.string().nullable(),
    /** Live status read from app-issues at request time; null when the ticket does not exist yet. */
    ticketStatus: zod_1.z.string().nullable(),
    ticketPriority: zod_1.z.string().nullable(),
    /** Ids of the devices seen with this problem (capped with the row, 200). Lets the fleet list count open issues per till. */
    deviceIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** Only on a `deviceId` query: how often, and when last, THIS device hit the problem. */
    deviceCount: Int.optional(),
    deviceLastSeen: zod_1.z.number().int().optional(),
});
exports.DeviceHealthTimelineEventSchema = zod_1.z.object({
    eventId: zod_1.z.string(),
    kind: exports.DeviceHealthKindSchema,
    ts: zod_1.z.number().int(),
    count: Int,
    message: zod_1.z.string(),
    screen: zod_1.z.string(),
    appVersion: zod_1.z.string(),
    bundleVersion: zod_1.z.string().nullable(),
    issueId: zod_1.z.string().nullable(),
    detail: zod_1.z.record(zod_1.z.string(), DetailValue),
});
//# sourceMappingURL=deviceHealth.js.map